<template>
  <section :class="['plan-row-item__wrapper', small && 'plan-row-item__wrapper--small']">
    <BestValueRibbon v-if="isBestValue" class="plan-row-item__ribbon" />
    <template v-if="isRegularPlanCard">
      <div
        :class="['plan-row-item', isBestValue && 'plan-row-item--best-value-square']"
        @click="handlePlanClick(plan)"
        @mouseover="isHovered = true"
        @mouseleave="isHovered = false"
        @mousedown="isClicked = true"
        @mouseup="isClicked = false"
      >
        <div class="plan-row-item__info-title-container" v-if="plan.infoTitle">
          <PlanRowItemInfoPill :info="plan.infoTitle" :left="isBestValue" />
        </div>
        <div class="plan-row-item__title-container">
          <div class="plan-row-item__title">
            {{ normalizedPlanName }}
          </div>
          <div v-if="isMultipleCard" class="plan-row-item__toggle-container">
            <div
              v-for="(plan, index) in toggablePlans"
              :key="index"
              :class="[
                'plan-row-item__toggle-plan',
                plan.selected && 'plan-row-item__toggle-plan--selected',
              ]"
              @click="selectNestedPlan($event, plan)"
            >
              {{ plan.name }}
            </div>
          </div>
        </div>
        <PlanRowItemPrice :plan="plan" />
        <div
          :class="[
            'plan-row-item__subtitle-container',
            isPremiumPlusPlan
              ? 'plan-row-item__subtitle-container--margin-7'
              : 'plan-row-item__subtitle-container--margin-20',
          ]"
        >
          <PlanRowItemSubtitle
            :subtitle="plan.subtitle"
            :tooltip="plan.tooltip"
            :displayUnderline="isPremiumPlusPlan"
          />
        </div>
        <div class="plan-row-item__details">
          <PlanListItemDetails :plan="plan" />
        </div>
        <div class="plan-row-item__radio-wrapper">
          <div
            :class="[
              'plan-row-item__radio-wrapper--button',
              'btn',
              'btn--full-mobile',
              isSelected && !isClicked ? 'btn--primary' : 'btn--secondary-no-active',
            ]"
          >
            <RadioButton
              :isSelected="isSelected"
              :primary="isSelected"
              :parentHover="isHovered"
              :parentClicked="isClicked"
            />
            <span>Get {{ plan.name }}</span>
          </div>
        </div>
        <div class="plan-row-item__bottom-container">
          <PlanRowItemBottom :link="plan.link" :displayLimited="isPremiumPlusPlan" />
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { updateCart, routesRules } from '@/helpers/mixins'
import PlanListItemDetails from '@/components/plan-item-card/PlanListItemDetails.vue'
import PlanRowItemPrice from '@/components/plan-item-card/PlanRowItemPrice.vue'
import CONSTANTS from '@/constants'
import RadioButton from '@/components/shared/RadioButton.vue'
import PlanRowItemSubtitle from '@/components/plan-item-card/PlanRowItemSubtitle.vue'
import BestValueRibbon from './BestValueRibbon.vue'
import PlanRowItemBottom from './PlanRowItemBottom.vue'
import PlanRowItemInfoPill from './PlanRowItemInfoPill.vue'

const { PLANS, LEAP_YEAR_PROMO_PRODUCT_BUNDLES, TCS_PROMO_PRODUCT_BUNDLES } = CONSTANTS
const { MONTHLY, ANNUAL, PREMIUM_PLUS } = PLANS
const { INE_TCS_BUNDLE } = TCS_PROMO_PRODUCT_BUNDLES

export default {
  name: 'PlanRowItem',
  mixins: [updateCart, routesRules],
  props: {
    plan: {
      type: Object,
      required: true,
    },
    currentPlans: {
      type: Array,
      required: false,
      default: () => [],
    },
    index: {
      type: Number,
      required: false,
      default: 0,
    },
    small: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isBoogoCard: this.plan.merchant_id === PLANS.INE_PREMIUM_BOOGO_2Y,
      isHovered: false,
      isClicked: false,
    }
  },
  components: {
    PlanListItemDetails,
    PlanRowItemPrice,
    RadioButton,
    BestValueRibbon,
    PlanRowItemSubtitle,
    PlanRowItemBottom,
    PlanRowItemInfoPill,
  },
  computed: {
    ...mapState({
      isPremiumMonthlyPromoActive: state => state.app.isPremiumMonthlyPromoActive,
    }),
    ...mapGetters({
      isBusinessUser: 'business/isBusinessUser',
      isBlackFridayActive: 'app/isBlackFridayActive',
      isTCSPromoEnabled: 'featureFlags/isTCSPromoEnabled',
      selectedPlan: 'shoppingCart/selectedPlan',
      premiumPlusEnabled: 'featureFlags/premiumPlusEnabled',
    }),
    isSelected() {
      // for bundles that should look like plans, add exceptions here
      // so the radio button can change state to isSelected = true
      if (
        [
          ...Object.values(LEAP_YEAR_PROMO_PRODUCT_BUNDLES),
          ...Object.values(TCS_PROMO_PRODUCT_BUNDLES),
        ].includes(this.plan.merchant_id)
      ) {
        return this.plan.merchant_id === this.selectedBundle.merchant_id
      }
      return this.plan.merchant_id === this.selectedPlan.merchant_id
    },
    isBestValue() {
      return this.plan.isBestValue
    },
    isFundamentalsAnnual() {
      return this.plan.merchant_id === PLANS.ANNUAL
    },
    isPremiumPlan() {
      return [PLANS.PREMIUM, INE_TCS_BUNDLE].includes(this.plan.merchant_id)
    },
    normalizedPlanName() {
      if (this.isBoogoCard) return 'Premium'
      switch (this.plan.merchant_id) {
        case PLANS.ANNUAL:
        case PLANS.MONTHLY:
          return 'Fundamentals'
        default:
          return this.plan.name
      }
    },
    isRegularPlanCard() {
      return ![PLANS.INE_PREMIUM_BOOGO_2Y].includes(this.plan.merchant_id)
    },
    isMultipleCard() {
      return this.plan.selected !== undefined
    },
    toggablePlans() {
      const toggablePlans = [ANNUAL, MONTHLY]
      return toggablePlans.map(merchant_id => {
        const name = merchant_id.replace('fundamentals_', ' ').toUpperCase()
        return merchant_id === this.plan.merchant_id
          ? { name, merchant_id, selected: this.plan.selected }
          : { name, merchant_id, selected: !this.plan.selected }
      })
    },
    hasItemsImage() {
      return this.plan.merchant_id === ANNUAL
    },
    showTCSPromo() {
      return this.isTCSPromoEnabled && this?.plan?.merchant_id === INE_TCS_BUNDLE
    },
    isPremiumPlusPlan() {
      return this.plan.merchant_id === PREMIUM_PLUS
    },
  },
  methods: {
    ...mapActions({
      addAddonsToPlanInCart: 'shoppingCart/addAddonsToPlanInCart',
    }),
    isFundamentalPlan(plan) {
      return [ANNUAL, MONTHLY].includes(plan.merchant_id)
    },
    selectNestedPlan(event, plan) {
      event.stopPropagation()
      const selectedPlan = this.currentPlans.find(p => p.merchant_id === plan.merchant_id)
      if (
        selectedPlan &&
        this.isFundamentalPlan(selectedPlan) &&
        this.isFundamentalPlan(this.selectedPlan)
      ) {
        this.updateSelectedPlan(selectedPlan)
      }
      this.$emit('toggle-card', this.toggablePlans, plan.merchant_id)
    },
    async selectPremiumPlusPlan() {
      const sdAddOn = this.skillDivePlan.addOns[0]
      this.plan.addOnsOnCart = []

      if (!this.isSelected) {
        this.updateSelectedPlan(this.plan)
        await this.addAddonsToPlanInCart({
          plan: this.plan,
          addOn: sdAddOn,
        })

        if (this.asAddOns) this.plan.selectedAsAddOns = true
      } else if (this.plan.addOnsOnCart.length > 1) {
        this.removeAddOnFromPlan(this.plan, sdAddOn)
        if (this.asAddOns && this.plan.addOnsOnCart.length === 0) this.plan.selectedAsAddOns = false
      } else {
        this.removeAddOnFromPlan(this.plan, sdAddOn)
      }
      if (this.plan.addOnsOnCart.length > 0) {
        this.removeProductFromCart(this.plan)
        this.updateSelectedPlan(this.plan)
      } else {
        this.removeProductFromCart(this.plan)
      }
    },
    handlePlanClick(plan) {
      if (this.isPremiumPlusPlan) {
        this.selectPremiumPlusPlan()
      } else {
        this.updateSelectedPlan(plan)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.plan-row-item {
  $border: 2;
  $border-unit: #{$border}px;
  background: var(--mid-gray-gradient);
  border-radius: 20px;
  padding: 20px;
  position: relative;
  cursor: pointer;
  transition: all 1s ease-out;
  width: 100%;
  &:hover {
    background: var(--mid-gray-translucid-gradient);
  }
  &:active {
    transition: all 0.5s ease-out;
    background: var(--charcoal-gradient-two);
  }

  @include tablet {
    padding: 20px;
  }
  &__ribbon {
    position: absolute;
    top: 0;
    right: 20px;
    z-index: 1;
  }
  &--best-value-square {
    border: solid $border-unit var(--dark-orchid);
  }
  &__wrapper {
    position: relative;
    width: 100%;
    min-width: 342px;
    display: flex;
    justify-content: center;
    flex-direction: column;
    @include mobile {
      min-width: 354px;
    }
    @include desktop {
      min-width: 542.5px;
    }
    &--small {
      min-width: auto;
    }
  }
  &__header,
  &__info-title-container {
    margin-bottom: 40px;
  }
  &__title-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    @include desktop {
      height: unset;
    }
  }
  &__title {
    color: var(--white);
    font-weight: 700;
    font-size: 24px;
    line-height: 32px;
  }

  &__subtitle-container {
    &--margin-7 {
      margin-bottom: 7px;
    }
    &--margin-20 {
      margin-bottom: 20px;
    }
  }

  &__toggle-container {
    display: flex;
    border-radius: 20px;
    background: var(--tundora-gray);
  }
  &__toggle-plan {
    font-size: rem(12px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(14px);
    padding: 4px 10px;
    &--selected {
      background: var(--strong-light-green);
      color: var(--gray-3);
      border-radius: 20px;
    }
  }
  &__bottom-content {
    margin-top: 30px;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    &--boogo-card {
      justify-content: flex-end;
    }
    &--bottom-highlight {
      height: 35px;
      margin-bottom: 70px;
    }
  }
  &__learn-more {
    margin-bottom: 12px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: nowrap;
    &-link {
      color: var(--purple-pale);
      font-size: rem(14px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(20px);
      text-decoration: none;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    &--justify-f-end {
      justify-content: flex-end;
    }
  }
  &__not-included {
    color: var(--light-blue-gray);
    font-size: rem(12px);
    font-style: italic;
    font-weight: 450;
    line-height: rem(14px);
    margin-bottom: 3px;
  }
  &__radio-wrapper {
    margin-top: 20px;
    &--button {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 10px;
    }
  }
  &__divider {
    border-bottom: 1px solid var(--mid-gray-translucid);
    margin-bottom: 30px;
    @include mobile {
      margin-bottom: 40px;
    }
    &--fundamentals_annual {
      width: calc(100% - 153px);
    }
    &--traincertifysave2025-1,
    &--ine_premium,
    &--bah_premium_plus {
      width: calc(100% - 170px);
    }
  }
  &__details {
    position: relative;
    &-vouchers-container {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      position: absolute;
      top: -42px;
      right: 0;
      @include mobile {
        top: -50px;
      }
    }
    &-title {
      color: var(--white);
      font-size: rem(14px);
      font-weight: 450;
      line-height: rem(20px);
    }
    &-badges-container {
      display: flex;
      margin-top: 30px;
      &--off {
        margin-top: 11px;
      }
    }
    &-voucher {
      width: 70px;
      &--one {
        width: 130px;
      }
    }
    &-icon {
      width: 16px;
      margin: 0 2px;
    }
  }
  &__bottom-container {
    margin-top: 10px;
  }
}

::v-deep .plan-row-item__header-text--fundamentals-annual {
  color: var(--purple-pale);
  font-weight: 700;
}
::v-deep .plan-row-item__bottom-highlight--you-save {
  font-size: rem(16px);
  font-style: normal;
  font-weight: 700;
  line-height: rem(23px);
}
</style>
